// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import type { FormEvent } from 'react';
import { useState } from 'react';
import { Button } from './Button';
import { Input } from './Input';
import { api } from '../lib/api';
import { useAuthStore } from '../store/authStore';
import type { User } from '../types/auth';

interface LoginFormProps {
  onSuccess?: () => void;
  onSwitchToRegister?: () => void;
}

export function LoginForm({ onSuccess, onSwitchToRegister }: LoginFormProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setAuth } = useAuthStore();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const response = await api.login({ username, password });

      const user: User = {
        user_id: response.user_id,
        username: response.username,
        permissions: response.permissions || ['user'],
        theme_preference: response.theme_preference,
        theme_primary_color: response.theme_primary_color,
        profile_emoji: response.profile_emoji,
        namespace: response.namespace,
      };

      setAuth(user, response.access_token, response.refresh_token);

      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
      <Input
        label="Username"
        type="text"
        value={username}
        onChange={e => setUsername(e.target.value)}
        placeholder="Enter username"
        required
        minLength={3}
        maxLength={50}
        autoComplete="username"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        // eslint-disable-next-line jsx-a11y/no-autofocus -- Intentional UX: focus username field on form load
        autoFocus
        data-testid="login-username"
      />
      <Input
        label="Password"
        type="password"
        value={password}
        onChange={e => setPassword(e.target.value)}
        placeholder="Enter password"
        required
        autoComplete="current-password"
        data-testid="login-password"
      />
      {error && (
        <p className="text-sm font-bold" style={{ color: '#EF4444' }}>
          {error}
        </p>
      )}
      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full font-bold"
        data-testid="login-submit"
      >
        {isSubmitting ? 'Signing in...' : 'Sign In'}
      </Button>

      {onSwitchToRegister && (
        <p className="text-center text-sm mt-4 font-bold" style={{ color: '#000000' }}>
          Don't have an account?{' '}
          <button
            type="button"
            onClick={onSwitchToRegister}
            className="underline hover:no-underline font-bold"
            style={{ color: '#FF6EC7' }}
          >
            Register
          </button>
        </p>
      )}
    </form>
  );
}
